import { useCallback, useRef } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useHistory } from 'react-router-dom';
import { apiUrl, apiHeaders } from '../lib/api';
import type { MessageDraft } from '../lib/localStore';
import {
  listPendingMigrationDrafts,
  markMigrationComplete,
  upsertDraft,
  shouldRunMigration,
} from '../lib/localStore';
import { encryptTextV2 } from '../lib/chatCrypto';
import { useI18n } from '../i18n/I18nProvider';

interface ContactResponse {
  id: string;
  linkedUserId: string | null;
}

interface ConversationResponse {
  id: string;
}

async function createContact(
  token: string,
  draft: MessageDraft
): Promise<ContactResponse> {
  const res = await fetch(apiUrl('/api/contacts'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...apiHeaders(token) },
    body: JSON.stringify({
      name: draft.recipient?.name || draft.recipient?.email || 'Contact',
      emails: draft.recipient?.email ? [draft.recipient.email] : [],
      phones: draft.recipient?.phone ? [draft.recipient.phone] : [],
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => undefined);
    throw new Error(body?.message || `Contact failed (${res.status})`);
  }
  return res.json() as Promise<ContactResponse>;
}

async function createDirectChat(
  token: string,
  participantUserId: string
): Promise<string> {
  const res = await fetch(apiUrl('/api/chat/conversations'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...apiHeaders(token) },
    body: JSON.stringify({ participantUserId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => undefined);
    throw new Error(body?.message || `Chat failed (${res.status})`);
  }
  const data = (await res.json()) as ConversationResponse;
  return data.id;
}

async function sendTextMessage(
  token: string,
  conversationId: string,
  draft: MessageDraft
): Promise<void> {
  let content = draft.body;
  if (draft.mode === 'coded' && draft.encodeKey) {
    content = await encryptTextV2(draft.body, draft.encodeKey);
  }
  const res = await fetch(
    apiUrl(`/api/chat/conversations/${conversationId}/messages`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...apiHeaders(token) },
      body: JSON.stringify({
        type: 'TEXT',
        content,
        encodeKey: draft.mode === 'coded' ? draft.encodeKey : undefined,
        isHidden: draft.mode === 'ghost',
        revealType: draft.mode === 'ghost' ? 'MANUAL' : undefined,
      }),
    }
  );
  if (!res.ok) {
    throw new Error(`Send failed (${res.status})`);
  }
}

export function useDraftMigration() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const history = useHistory();
  const { t } = useI18n();
  const runningRef = useRef(false);

  const migrateDraft = useCallback(
    async (draft: MessageDraft): Promise<{ conversationId?: string; sent: boolean }> => {
      const token = await getToken();
      if (!token) throw new Error(t('quickCompose.authRequired'));

      let conversationId = draft.cloudConversationId;
      let sent = false;

      if (draft.recipient?.email || draft.recipient?.phone) {
        const contact = await createContact(token, draft);
        if (contact.linkedUserId) {
          conversationId = await createDirectChat(token, contact.linkedUserId);
          await sendTextMessage(token, conversationId, draft);
          sent = true;
        }
      }

      upsertDraft({
        ...draft,
        status: sent ? 'sent' : 'migrated',
        cloudConversationId: conversationId,
        updatedAt: new Date().toISOString(),
      });

      return { conversationId, sent };
    },
    [getToken, t]
  );

  const migrateAllPending = useCallback(async (): Promise<number> => {
    if (runningRef.current || !user?.id) return 0;
    const pending = listPendingMigrationDrafts().filter((d) => d.body.trim());
    if (pending.length === 0) return 0;

    runningRef.current = true;
    let migrated = 0;
    try {
      for (const draft of pending) {
        try {
          await migrateDraft(draft);
          migrated += 1;
        } catch (err) {
          console.error('[draftMigration] failed for draft', draft.id, err);
        }
      }
      if (migrated > 0) {
        markMigrationComplete(user.id);
      }
    } finally {
      runningRef.current = false;
    }
    return migrated;
  }, [migrateDraft, user?.id]);

  const sendDraft = useCallback(
    async (draft: MessageDraft) => {
      const result = await migrateDraft(draft);
      if (result.conversationId) {
        history.push(`/chat/${result.conversationId}`);
      } else if (!result.sent) {
        throw new Error(t('quickCompose.recipientNotRegistered'));
      }
    },
    [history, migrateDraft, t]
  );

  const needsMigration = user?.id ? shouldRunMigration(user.id) : false;

  return { migrateDraft, migrateAllPending, sendDraft, needsMigration };
}

export default useDraftMigration;
